import React from 'react';
import ManCrossKpiCard         from '../components/ManCrossKpiCard';
import ManCrossInstrumentPanel from '../components/ManCrossInstrumentPanel';
import ManCrossSemaphorePanel  from '../components/ManCrossSemaphorePanel';
import ManCrossPageFooter      from '../components/ManCrossPageFooter';
import {
    SUMMARY_SEMAPHORE_ROWS,
    KPI_TONE_STYLES,
    formatRounded,
    formatPercent,
    getCoveragePercent,
    countBelowThreshold,
    toDimensionRows,
    getStatusTone,
    getRiskLabel,
} from '../shared/managerialUtils';

// ─── Coverage legend (explainer box) ─────────────────────────────────────────
const COVERAGE_LEGEND = [
    { label: '≥ 80%',   desc: 'Resultados concluyentes' },
    { label: '60–79%',  desc: 'Resultados indicativos' },
    { label: '< 60%',   desc: 'No concluyente · Señalado' },
];

// ─── Conditional narrative builder ───────────────────────────────────────────
const buildNarrative = ({ kpis, topIashRisks, topIpraRisks, criticalCount }) => {
    const icp  = kpis?.cross_index;
    const nps  = kpis?.ipra_nps;
    const p1   = topIpraRisks[0];
    const p2   = topIashRisks[0];

    if ([icp, kpis?.iash_average, kpis?.ipra_average, nps].every((v) => v == null)) {
        return 'No hay datos suficientes en esta corrida para consolidar el resumen ejecutivo.';
    }

    const lines = [];

    if (criticalCount > 0 && icp != null) {
        lines.push(
            `La operación presenta ${criticalCount} brecha${criticalCount > 1 ? 's' : ''} crítica${criticalCount > 1 ? 's' : ''} con un ICP de ${formatRounded(icp)} puntos. Se requiere intervención priorizada.`
        );
    } else if (icp != null) {
        lines.push(`El ICP consolidado se ubica en ${formatRounded(icp)} puntos — liderazgo y autocuidado operan dentro de rangos aceptables.`);
    }

    if (p1) {
        lines.push(`Brecha IPRA prioritaria: ${p1.dimension} (${formatRounded(p1.average)}).`);
    }
    if (p2) {
        lines.push(`Alerta IASH: ${p2.dimension} (${formatRounded(p2.average)}).`);
    }
    if (nps != null) {
        lines.push(`NPS de seguridad: ${formatRounded(nps, { signed: true })}.`);
    }

    return lines.join(' ');
};

// ─── Component ────────────────────────────────────────────────────────────────
/**
 * Executive Summary page.
 *
 * Props:
 *   reportData   {object}  – full API response
 *   orgName      {string}  – organisation display name
 *   headerText   {string}  – top-right page header
 *   pageNumber   {number}  – displayed page index
 */
const ManCrossPageSummary = ({ reportData, orgName, headerText, pageNumber }) => {
    const payload     = reportData?.payload || {};
    const kpis        = payload?.kpis      || {};
    const iashTotals  = payload?.totals?.iash || {};
    const ipraTotals  = payload?.totals?.ipra || {};

    const iashRows = toDimensionRows(iashTotals?.average_by_dimension);
    const ipraRows = toDimensionRows(ipraTotals?.average_by_dimension);

    const topIashRisks = [...iashRows].filter((r) => r.average != null).sort((a, b) => a.average - b.average).slice(0, 3);
    const topIpraRisks = [...ipraRows].filter((r) => r.average != null).sort((a, b) => a.average - b.average).slice(0, 3);

    const iashCoverage  = getCoveragePercent(iashTotals);
    const ipraCoverage  = getCoveragePercent(ipraTotals);
    const totalDims     = iashRows.length + ipraRows.length;
    const criticalCount = countBelowThreshold(iashRows, 50) + countBelowThreshold(ipraRows, 65);

    const narrative = buildNarrative({ kpis, topIashRisks, topIpraRisks, criticalCount });

    const kpiStrip = [
        {
            key:    'critical-dimensions',
            value:  totalDims ? `${criticalCount}/${totalDims}` : '-',
            label:  'Brechas críticas',
            badge:  criticalCount > 0 ? `${criticalCount} en rojo` : 'Sin criticidad',
            colors: KPI_TONE_STYLES[criticalCount > 0 ? 'critical' : 'low'],
        },
        {
            key:    'ipra-average',
            value:  formatRounded(kpis?.ipra_average),
            label:  'IPRA - Liderazgo',
            badge:  getRiskLabel(kpis?.ipra_average, 'ipra').toUpperCase(),
            colors: KPI_TONE_STYLES[getStatusTone(kpis?.ipra_average, 'ipra')],
        },
        {
            key:    'iash-average',
            value:  formatRounded(kpis?.iash_average),
            label:  'IASH - Autocuidado',
            badge:  getRiskLabel(kpis?.iash_average, 'iash').toUpperCase(),
            colors: KPI_TONE_STYLES[getStatusTone(kpis?.iash_average, 'iash')],
        },
        {
            key:    'ipra-nps',
            value:  formatRounded(kpis?.ipra_nps, { signed: true }),
            label:  'NPS Seguridad',
            badge:  Number(kpis?.ipra_nps) >= 0 ? 'POSITIVO' : 'NEGATIVO',
            colors: KPI_TONE_STYLES[getStatusTone(kpis?.ipra_nps, 'nps')],
        },
        {
            key:    'cross-index',
            value:  formatRounded(kpis?.cross_index),
            label:  'ICP Planta',
            badge:  getRiskLabel(kpis?.cross_index).toUpperCase(),
            colors: KPI_TONE_STYLES[getStatusTone(kpis?.cross_index)],
        },
    ];

    const instrumentCards = [
        {
            key:     'iash',
            title:   'IASH',
            caption: 'Índice de Autocuidado en Seguridad y Salud',
            metrics: [
                { label: 'Dimensiones',      value: `${iashRows.length}` },
                { label: 'Escala',           value: '0 - 100' },
                { label: 'Universo',         value: iashTotals?.relationships_total    ?? '-' },
                { label: 'Muestra efectiva', value: iashTotals?.relationships_finished ?? '-' },
                { label: 'Cobertura',        value: formatPercent(iashCoverage) },
            ],
        },
        {
            key:     'ipra',
            title:   'IPRA',
            caption: 'Índice Predictivo de Riesgo Actitudinal',
            metrics: [
                { label: 'Dimensiones',      value: `${ipraRows.length}` },
                { label: 'Escala',           value: '0 - 100' },
                { label: 'Universo',         value: ipraTotals?.relationships_total    ?? '-' },
                { label: 'Muestra efectiva', value: ipraTotals?.relationships_finished ?? '-' },
                { label: 'Cobertura',        value: formatPercent(ipraCoverage) },
            ],
        },
    ];

    return (
        <div className="mgcr-summary-page">

            {/* ── Top-right header ── */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                {pageNumber ? <span className="mgcr-page-header-num">{pageNumber}</span> : null}
            </div>

            <div className="keep-blankspace" />

            {/* ── Title block ── */}
            <div className="mgcr-summary-title-wrap">
                <div className="mgcr-summary-title">Resumen Ejecutivo</div>
                <div className="mgcr-summary-subtitle">¿Qué medimos y qué encontramos?</div>
            </div>

            {/* ── Main content board ── */}
            <div className="mgcr-summary-board">
                <div className="mgcr-summary-board-label">Desempeño y seguridad de la operación</div>

                {/* Top row: explainer + right col (KPI strip + semáforo) */}
                <div className="mgcr-summary-board-top">

                    <div className="mgcr-explainer">
                        <div className="mgcr-explainer-title">Lo que encuentra en este informe</div>
                        <div className="mgcr-explainer-line">
                            <strong>IPRA</strong> mide cómo lideran la seguridad los supervisores (360°).
                        </div>
                        <div className="mgcr-explainer-line">
                            <strong>IASH</strong> mide cómo se cuidan los operarios (autoreporte).
                        </div>
                        <div className="mgcr-explainer-line">
                            <strong>IVC</strong> cruza ambos + confianza para detectar co-falla de barreras.
                        </div>
                        <div className="mgcr-explainer-line">
                            <strong>El valor está en el cruce, no en indicadores aislados.</strong>
                        </div>
                        <div className="mgcr-explainer-callout">
                            {COVERAGE_LEGEND.map((row) => (
                                <div key={row.label} className="mgcr-legend-row">
                                    <span className="mgcr-legend-range">{row.label}</span>
                                    <span className="mgcr-legend-desc">{row.desc}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mgcr-explainer-note">
                            <strong>Esto implica que:</strong> datos de áreas con cobertura &lt;60% se presentan con advertencia y se excluyen de rankings comparativos.
                        </div>
                    </div>

                    <div className="mgcr-right-col">
                        <div className="mgcr-kpi-strip">
                            {kpiStrip.map((kpi) => (
                                <ManCrossKpiCard key={kpi.key} {...kpi} />
                            ))}
                        </div>
                        <ManCrossSemaphorePanel rows={SUMMARY_SEMAPHORE_ROWS} />
                    </div>
                </div>

                {/* Bottom row: IASH + IPRA + highlights */}
                <div className="mgcr-summary-board-bottom">
                    {instrumentCards.map((card) => (
                        <ManCrossInstrumentPanel key={card.key} {...card} />
                    ))}

                    <div className="mgcr-panel mgcr-highlights-panel">
                        <div className="mgcr-panel-title">Hallazgo principal</div>
                        <div className="mgcr-narrative">{narrative}</div>
                        {topIpraRisks[0] && (
                            <div className="mgcr-highlight-item">
                                <div className="mgcr-highlight-label">Brecha IPRA prioritaria</div>
                                <div className="mgcr-highlight-value">
                                    {topIpraRisks[0].dimension} ({formatRounded(topIpraRisks[0].average)})
                                </div>
                            </div>
                        )}
                        {topIashRisks[0] && (
                            <div className="mgcr-highlight-item">
                                <div className="mgcr-highlight-label">Brecha IASH prioritaria</div>
                                <div className="mgcr-highlight-value">
                                    {topIashRisks[0].dimension} ({formatRounded(topIashRisks[0].average)})
                                </div>
                            </div>
                        )}
                        {(iashCoverage != null || ipraCoverage != null) && (
                            <div className="mgcr-highlight-item">
                                <div className="mgcr-highlight-label">Cobertura</div>
                                <div className="mgcr-highlight-value">
                                    IASH {formatPercent(iashCoverage)} · IPRA {formatPercent(ipraCoverage)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="keep-blankspace" />

            {/* ── Decorative footer band ── */}
            <ManCrossPageFooter />

        </div>
    );
};

export default ManCrossPageSummary;
